import React, { useState, useCallback, useEffect } from 'react';
import { ActivityIndicator, Keyboard } from 'react-native';
import styled from '@emotion/native';
import { TextInput } from 'react-native-paper';
import { BodyText } from '@/shared/components/StyledText';
import {
  green,
  red,
  placeholder,
  white,
  lightGreyAlt,
  lightGrey,
} from '@/shared/styles/colors';
import { useLazyQuery } from '@apollo/client';
import { VALIDATE_DISCOUNT_CODE } from '@/shared/queries';
import { QueryResult } from '@/shared/types/generated';
import SentryService from '@/services/sentry/Sentry.service';
import AddIcon from '../AddIcon';
import RemoveIcon from '../RemoveIcon';
import SmallCheckMark from '@/assets/images/SmallCheckMark.png';

interface PromoCodeDisplayProps {
  onDiscountApplied?: (discountPercentage: number, codeText: string) => void;
  onDiscountRemoved?: () => void;
  stylistId: number | null;
  initialCodeText?: string;
  initialDiscountPercentage?: number;
  isReceiptItem?: boolean;
}

const PromoCodeDisplay: React.FC<PromoCodeDisplayProps> = ({
  onDiscountApplied,
  onDiscountRemoved,
  stylistId,
  initialCodeText,
  initialDiscountPercentage,
  isReceiptItem,
}) => {
  const [codeText, setCodeText] = useState(initialCodeText ?? '');
  const [error, setError] = useState<string | null>(null);
  const [appliedDiscount, setAppliedDiscount] = useState<number | null>(
    initialDiscountPercentage !== undefined ? initialDiscountPercentage : null,
  );

  useEffect(() => {
    setCodeText(initialCodeText ?? '');
  }, [initialCodeText]);

  useEffect(() => {
    setAppliedDiscount(
      initialDiscountPercentage !== undefined
        ? initialDiscountPercentage
        : null,
    );
  }, [initialDiscountPercentage]);

  const [validateDiscountCode, { loading }] = useLazyQuery<
    QueryResult<'validateDiscountCode'>
  >(VALIDATE_DISCOUNT_CODE, {
    fetchPolicy: 'no-cache',
    onError: (error) => {
      const errorMessage = error.message || 'Invalid promo code';
      setError(errorMessage);
      setAppliedDiscount(null);
      onDiscountRemoved?.();
    },
    onCompleted: (data) => {
      if (data?.validateDiscountCode?.valid) {
        const discountPercentage =
          data.validateDiscountCode.discountPercentage ?? 0;
        const validatedCodeText = codeText.trim().toUpperCase();
        setAppliedDiscount(discountPercentage);
        setError(null);
        onDiscountApplied?.(discountPercentage, validatedCodeText);
      } else {
        setError(data?.validateDiscountCode?.message || 'Invalid promo code');
        setAppliedDiscount(null);
        onDiscountRemoved?.();
      }
    },
  });

  const handleValidate = useCallback(async () => {
    if (!codeText.trim()) {
      return;
    }

    Keyboard.dismiss();
    setError(null);
    try {
      await validateDiscountCode({
        variables: {
          input: {
            codeText: codeText.trim().toUpperCase(),
            stylistId,
          },
        },
      });
    } catch (error) {
      SentryService.captureError('Error validating discount code', error);
      setError('Failed to validate promo code');
      setAppliedDiscount(null);
      onDiscountRemoved?.();
    }
  }, [codeText, validateDiscountCode, stylistId, onDiscountRemoved]);

  const handleCodeChange = useCallback(
    (text: string) => {
      setCodeText(text);
      // Clear error and discount when user starts typing
      if (error) {
        setError(null);
      }
      if (appliedDiscount) {
        setAppliedDiscount(null);
        onDiscountRemoved?.();
      }
    },
    [error, appliedDiscount, onDiscountRemoved],
  );

  const handleRemove = useCallback(() => {
    Keyboard.dismiss();
    setCodeText('');
    setAppliedDiscount(null);
    setError(null);
    onDiscountRemoved?.();
  }, [onDiscountRemoved]);

  const hasSuccess = appliedDiscount !== null;
  const showButton =
    (codeText.trim().length > 0 || hasSuccess) && !isReceiptItem;

  const isEditable = !hasSuccess && !isReceiptItem;

  return (
    <Container>
      <InputContainer>
        <StyledInput
          value={codeText}
          onChangeText={handleCodeChange}
          placeholder="Enter Promo Code"
          placeholderTextColor={placeholder}
          mode="outlined"
          outlineStyle={inputBorderStyle}
          contentStyle={inputContentStyle}
          editable={isEditable}
          isEditable={isEditable}
          isReceiptItem={isReceiptItem}
          right={
            loading ? (
              <TextInput.Icon
                icon={() => <ActivityIndicator size="small" color={green} />}
                size={20}
              />
            ) : showButton ? (
              <TextInput.Icon
                icon={() =>
                  hasSuccess ? (
                    <RemoveIcon width={27} />
                  ) : (
                    <AddIcon width={27} />
                  )
                }
                size={20}
                onPress={hasSuccess ? handleRemove : handleValidate}
              />
            ) : null
          }
        />
      </InputContainer>
      {error && <ErrorText>{error}</ErrorText>}
      {hasSuccess && (
        <SuccessTextContainer>
          <Check source={SmallCheckMark} />
          <SuccessText>Offer Applied</SuccessText>
        </SuccessTextContainer>
      )}
    </Container>
  );
};

const Container = styled.View({
  flexDirection: 'column',
  alignItems: 'flex-end',
  minWidth: 150,
});

const InputContainer = styled.View({
  width: '100%',
  maxWidth: 210,
});

const StyledInput = styled(TextInput)<{
  isEditable: boolean;
  isReceiptItem?: boolean;
}>(({ isEditable, isReceiptItem }) => ({
  height: 39,
  fontSize: 14,
  width: 191,
  backgroundColor: isEditable ? white : lightGreyAlt,
  fontWeight: 'bold',
  textAlign: isReceiptItem ? 'center' : 'left',
}));

const inputBorderStyle = {
  borderWidth: 1,
  borderColor: lightGrey,
  borderRadius: 36,
};

const inputContentStyle = {
  fontSize: 14,
  paddingRight: 8,
};

const ErrorText = styled(BodyText)({
  fontSize: 11,
  color: red,
  marginTop: 4,
  textAlign: 'right',
  maxWidth: 210,
});

const SuccessText = styled(BodyText)({
  fontSize: 12,
  color: green,
  marginTop: 4,
  textAlign: 'right',
  fontWeight: '500',
  maxWidth: 210,
});

const SuccessTextContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'flex-end',
});

const Check = styled.Image({
  width: 32,
  height: 32,
});

export default PromoCodeDisplay;
